/**
 * P0 Support Ticket System
 * Create and manage support tickets
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
} from '@mui/material';
import {
  Support as SupportIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import supportApi from '../../api/supportApi';

const SupportTickets = () => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    priority: 'medium',
  });

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await supportApi.list();
      setTickets(response.results || response || []);
    } catch (error) {
      console.error('Error fetching support tickets:', error);
      toast.error('Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.subject.trim() || !formData.description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      await supportApi.create(formData);
      toast.success('Support ticket created successfully!');
      setDialogOpen(false);
      setFormData({
        subject: '',
        description: '',
        priority: 'medium',
      });
      fetchTickets();
    } catch (error) {
      console.error('Error creating support ticket:', error);
      toast.error(error.response?.data?.error || 'Failed to create support ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'default',
      medium: 'primary',
      high: 'warning',
      urgent: 'error',
    };
    return colors[priority] || 'default';
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'error',
      in_progress: 'warning',
      resolved: 'success',
      closed: 'default',
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      open: <CancelIcon />,
      in_progress: <ScheduleIcon />,
      resolved: <CheckCircleIcon />,
      closed: <CheckCircleIcon />,
    };
    return icons[status] || <SupportIcon />;
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Support Tickets</Typography>
        <Box>
          <IconButton onClick={fetchTickets} color="primary" sx={{ mr: 1 }}>
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
          >
            Create Ticket
          </Button>
        </Box>
      </Box>

      {/* Create Ticket Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Support Ticket</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  label="Priority"
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subject"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                placeholder="Please describe your issue in detail..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : <AddIcon />}
          >
            Create Ticket
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tickets List */}
      <Paper>
        <Box p={2}>
          <Typography variant="h6" gutterBottom>
            <SupportIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Your Support Tickets
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : tickets.length === 0 ? (
            <Alert severity="info">
              No support tickets yet. Create one to get help with any issues.
            </Alert>
          ) : (
            <List>
              {tickets.map((ticket) => (
                <ListItem
                  key={ticket.id}
                  sx={{
                    borderLeft: `3px solid`,
                    borderColor: `${getStatusColor(ticket.status)}.main`,
                    mb: 2,
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    flexDirection: 'column',
                    alignItems: 'stretch',
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          {ticket.ticket_number || `#${ticket.id?.slice(0, 8)}`}
                        </Typography>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {ticket.subject}
                        </Typography>
                      </Box>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        <Chip
                          label={ticket.priority}
                          size="small"
                          color={getPriorityColor(ticket.priority)}
                        />
                        <Chip
                          icon={getStatusIcon(ticket.status)}
                          label={ticket.status}
                          size="small"
                          color={getStatusColor(ticket.status)}
                        />
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {ticket.description}
                    </Typography>
                    <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                      <Typography variant="caption" color="text.secondary">
                        Created: {ticket.created_at
                          ? format(new Date(ticket.created_at), 'PPp')
                          : 'Date not available'}
                      </Typography>
                      {ticket.clinic_name && (
                        <Chip
                          label={`Clinic: ${ticket.clinic_name}`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                    {ticket.resolution && (
                      <Box mt={2} p={2} bgcolor="success.light" borderRadius={1}>
                        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                          Resolution:
                        </Typography>
                        <Typography variant="body2">{ticket.resolution}</Typography>
                        {ticket.resolved_at && (
                          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                            Resolved: {format(new Date(ticket.resolved_at), 'PPp')}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default SupportTickets;

